import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  flatOptions,
  recordSymbolUse,
  removeSymbolUse,
  symbolSections,
  useSettings,
  type SymbolOption,
} from "@helm/core";
import { settingsSync } from "../session";
import { Icon } from "./Icon";

/** Record a pick in the synced MRU (the dropdown's Recent section). */
export function noteSymbolUse(symbol: string): void {
  const mru = useSettings.getState().settings.symbol_mru;
  void settingsSync.update({ symbol_mru: recordSymbolUse(mru, symbol, Date.now()) });
}

/**
 * The symbol dropdown of the DOM, the order pad and every chart pane (owner 2026-09-28; one
 * component everywhere). The button shows the symbol; the menu has a search box (anything the
 * catalog knows, or an explicit contract as typed), **Recent** (the user's picks, newest first,
 * with use counts, synced per user, × to remove) and **Common futures** by asset class. A root
 * applies the front month (resolved by the gateway); a contract (NQH7) is kept. Keyboard: type,
 * ↑ / ↓, Enter, Esc. `onPick` applies the symbol (to the module, and to its link group when
 * linked — the caller decides); the pick is recorded in the MRU here.
 */
export function SymbolDropdown({
  symbol,
  onPick,
  label = "Symbol",
  title,
  compact = false,
}: {
  symbol: string | null;
  onPick: (symbol: string) => void;
  label?: string;
  title?: string;
  compact?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={btn}
        type="button"
        className={`symdd-btn${compact ? " symdd-compact" : ""}${open ? " active" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${symbol ?? "none"} — change`}
        title={title ?? "Change symbol: search, recent, or common futures"}
        data-symbol={symbol ?? ""}
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        <span className="symdd-sym">{symbol ?? "—"}</span>
        <Icon name="chevronDown" size={11} />
      </button>
      {open && (
        <SymbolMenu
          anchor={btn.current}
          current={symbol}
          onPick={(s) => {
            setOpen(false);
            noteSymbolUse(s);
            onPick(s);
          }}
          onClose={() => {
            setOpen(false);
            btn.current?.focus();
          }}
        />
      )}
    </>
  );
}

function SymbolMenu({
  anchor,
  current,
  onPick,
  onClose,
}: {
  anchor: HTMLElement | null;
  current: string | null;
  onPick: (s: string) => void;
  onClose: () => void;
}): React.JSX.Element | null {
  const id = useId();
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const box = useRef<HTMLDivElement>(null);
  const mru = useSettings((s) => s.settings.symbol_mru);
  const sections = useMemo(() => symbolSections(q, mru), [q, mru]);
  const flat = useMemo(() => flatOptions(sections), [sections]);
  const doc = anchor?.ownerDocument ?? document;
  const win = doc.defaultView ?? window;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useLayoutEffect(() => {
    const r = anchor?.getBoundingClientRect();
    if (!r) return;
    const w = 300;
    const left = Math.max(4, Math.min(r.left, win.innerWidth - w - 4));
    setPos({ left, top: r.bottom + 4 });
  }, [anchor, win]);
  useEffect(() => {
    const onDown = (e: PointerEvent): void => {
      const t = e.target as Node | null;
      if (t && (box.current?.contains(t) || anchor?.contains(t))) return;
      closeRef.current();
    };
    win.addEventListener("pointerdown", onDown, true);
    return () => {
      win.removeEventListener("pointerdown", onDown, true);
    };
  }, [anchor, win]);
  useEffect(() => {
    box.current?.querySelector(`[data-idx="${String(hi)}"]`)?.scrollIntoView({ block: "nearest" });
  }, [hi]);

  const pick = (o: SymbolOption | undefined): void => {
    if (o) onPick(o.symbol);
  };
  let idx = -1;
  return createPortal(
    <div
      ref={box}
      className="popover symdd-menu"
      role="dialog"
      aria-label="Choose a symbol"
      style={{ position: "fixed", left: pos.left, top: pos.top }}
    >
      <input
        autoFocus
        className="input input-sm"
        value={q}
        placeholder="Search: NQ, crude, NQH7…"
        aria-label="Search symbols"
        aria-controls={`${id}-list`}
        aria-activedescendant={flat.length > 0 ? `${id}-o${String(hi)}` : undefined}
        spellCheck={false}
        onChange={(e) => {
          setQ(e.target.value);
          setHi(0);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHi((h) => Math.min(flat.length - 1, h + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHi((h) => Math.max(0, h - 1));
          } else if (e.key === "Enter") {
            e.preventDefault();
            pick(flat[hi]);
          } else if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }
        }}
      />
      <div id={`${id}-list`} className="symdd-list" role="listbox" aria-label="Symbols">
        {sections.map((sec) => (
          <div key={sec.id} role="group" aria-label={sec.title} className="symdd-section">
            <div className="symdd-title">{sec.title}</div>
            {sec.options.map((o) => {
              idx += 1;
              const i = idx;
              return (
                <div
                  key={`${sec.id}:${o.symbol}`}
                  id={`${id}-o${String(i)}`}
                  data-idx={i}
                  role="option"
                  aria-selected={i === hi}
                  className={`symdd-opt${i === hi ? " active" : ""}${o.symbol === current ? " current" : ""}`}
                  onPointerEnter={() => {
                    setHi(i);
                  }}
                  onClick={() => {
                    pick(o);
                  }}
                >
                  <span className="sym">{o.symbol}</span>
                  <span className="symdd-name">{o.name}</span>
                  {o.uses !== undefined ? (
                    <span className="symdd-uses muted num" title={`Used ${String(o.uses)}×`}>
                      {o.uses}×
                      <button
                        type="button"
                        className="symdd-remove"
                        aria-label={`Remove ${o.symbol} from recent`}
                        title="Remove from recent"
                        onClick={(e) => {
                          e.stopPropagation();
                          const m = useSettings.getState().settings.symbol_mru;
                          void settingsSync.update({ symbol_mru: removeSymbolUse(m, o.symbol) });
                        }}
                      >
                        <Icon name="close" size={10} />
                      </button>
                    </span>
                  ) : (
                    <span className="muted symdd-ex">{o.entry?.exchange ?? ""}</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        {flat.length === 0 && <div className="muted symdd-empty">No match</div>}
      </div>
    </div>,
    doc.body,
  );
}
